import { createRootRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect } from "react";
import { getAccessToken } from "@/lib/auth";

import { TanStackRouterDevtools } from "@tanstack/react-router-devtools";
import { PersistQueryClientProvider } from "@tanstack/react-query-persist-client";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { I18nProvider } from "@lingui/react";
import { queryClient, persister } from "@/lib/api/query-client";
import { i18n } from "@/lib/i18n";
import { AppLayout } from "../layout";
import "../../index.css";
import { useAuth } from "@/hooks/useAuth";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Trans } from "@lingui/macro";
import { TourProvider } from "@/tours";
import { TourManager } from "@/components/tours";
import { ThemeProvider } from "@/contexts/ThemeContext";

export const Route = createRootRoute({
  component: RootComponent,
  notFoundComponent: NotFoundPage,
});

function RootComponent() {
  const inner = (
    <TourProvider>
      <AuthCacheInitializer />
      <AppLayout />
      <TourManager />
      {import.meta.env.DEV && (
        <>
          <TanStackRouterDevtools />
          <ReactQueryDevtools initialIsOpen={false} />
        </>
      )}
    </TourProvider>
  );

  return (
    <I18nProvider i18n={i18n}>
      <ThemeProvider>
        <PersistQueryClientProvider
          client={queryClient}
          persistOptions={{ persister }}
        >
          {inner}
        </PersistQueryClientProvider>
      </ThemeProvider>
    </I18nProvider>
  );
}

function AuthCacheInitializer() {
  useEffect(() => {
    // If there's no access token, ensure the auth user cache is cleared
    const hasToken = !!getAccessToken();
    if (!hasToken) {
      queryClient.setQueryData(["auth", "user"], null);
    } else if (sessionStorage.getItem('yeschef_oauth_redirect')) {
      // Fresh OAuth redirect: the persisted cache may have a stale null user entry
      // (e.g. from a previous logout). Invalidate it so getCurrentAuthUser re-runs.
      sessionStorage.removeItem('yeschef_oauth_redirect');
      queryClient.invalidateQueries({ queryKey: ["auth", "user"] });
    }
  }, []);

  return null;
}

function NotFoundPage() {
  const { isAuthenticated, isLoading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (!isLoading && !isAuthenticated) {
      navigate({ to: "/login" });
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading || !isAuthenticated) {
    return null;
  }

  return (
    <div className="flex min-h-screen items-center justify-center p-4">
      <Card className="w-full max-w-md text-center">
        <CardHeader>
          <CardTitle className="text-4xl font-bold">404</CardTitle>
          <CardDescription>
            <Trans>Page not found</Trans>
          </CardDescription>
        </CardHeader>
        <CardContent>
          <p className="mb-4 text-(--color-text-secondary)">
            <Trans>This page does not exist.</Trans>
          </p>
          <Link to="/dashboard">
            <Button>
              <Trans>Go to Dashboard</Trans>
            </Button>
          </Link>
        </CardContent>
      </Card>
    </div>
  );
}
